import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DownloadJob {
  id: string;
  user_id: string;
  job_ids: string[];
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  total_images: number;
  processed_images: number;
  storage_path: string | null;
  file_size_bytes: number | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string | null;
}

export const useBackgroundDownload = () => {
  const [activeDownloads, setActiveDownloads] = useState<DownloadJob[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch active download jobs
  const fetchActiveDownloads = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('download_jobs')
      .select('*')
      .in('status', ['pending', 'processing', 'completed'])
      .order('created_at', { ascending: false })
      .limit(10);

    if (!error && data) {
      setActiveDownloads(data as DownloadJob[]);
    }
  }, []);

  // Start a background download
  const startBackgroundDownload = async (jobIds: string[]): Promise<string | null> => {
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to download');
        return null;
      }

      // Create download job record
      const { data: downloadJob, error: insertError } = await supabase
        .from('download_jobs')
        .insert({
          user_id: user.id,
          job_ids: jobIds,
          status: 'pending',
          progress: 0
        })
        .select()
        .single();

      if (insertError || !downloadJob) {
        console.error('Failed to create download job:', insertError);
        toast.error('Failed to start download');
        return null;
      }

      toast.success('Download started in background! You can close this tab.');

      // Trigger the edge function (fire and forget)
      supabase.functions.invoke('generate-download-zip', {
        body: { downloadJobId: downloadJob.id }
      }).catch(err => {
        console.error('Edge function error:', err);
      });

      // Refresh the list
      await fetchActiveDownloads();

      return downloadJob.id;
    } catch (error) {
      console.error('Failed to start background download:', error);
      toast.error('Failed to start download');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get download URL for completed job
  const getDownloadUrl = async (storagePath: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from('download-zips')
      .createSignedUrl(storagePath, 3600); // 1 hour expiry

    if (error) {
      console.error('Failed to get download URL:', error);
      toast.error('Failed to get download link');
      return null;
    }

    return data.signedUrl;
  };

  // Download completed ZIP (supports multi-part)
  const downloadCompletedZip = async (downloadJob: DownloadJob) => {
    if (downloadJob.status !== 'completed' || !downloadJob.storage_path) {
      toast.error('Download is not ready yet');
      return;
    }

    const storagePath = downloadJob.storage_path;

    if (storagePath.startsWith('multi:')) {
      // Multi-part download: "multi:N:basePath"
      const parts = storagePath.split(':');
      const partCount = parseInt(parts[1]);
      const basePath = parts[2];
      
      toast.info(`Downloading ${partCount} ZIP parts...`);
      
      for (let i = 1; i <= partCount; i++) {
        const partPath = `${basePath}_part${i}.zip`;
        const url = await getDownloadUrl(partPath);
        if (url) {
          const link = document.createElement('a');
          link.href = url;
          link.download = `jobs_${new Date(downloadJob.created_at).toISOString().split('T')[0]}_part${i}.zip`;
          link.click();
          // Small delay between downloads
          await new Promise(r => setTimeout(r, 500));
        }
      }
      toast.success(`${partCount} ZIP files downloaded!`);
    } else {
      // Single ZIP
      const url = await getDownloadUrl(storagePath);
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `jobs_${new Date(downloadJob.created_at).toISOString().split('T')[0]}.zip`;
        link.click();
        toast.success('Download started!');
      }
    }
  };

  // Delete download job and file
  const deleteDownloadJob = async (downloadJobId: string) => {
    const job = activeDownloads.find(d => d.id === downloadJobId);
    
    // Delete from storage if exists
    if (job?.storage_path) {
      await supabase.storage.from('download-zips').remove([job.storage_path]);
    }

    // Delete the record
    await supabase.from('download_jobs').delete().eq('id', downloadJobId);
    
    await fetchActiveDownloads();
    toast.success('Download removed');
  };

  // Poll for updates
  useEffect(() => {
    fetchActiveDownloads();

    // Poll every 5 seconds if there are active downloads
    const interval = setInterval(() => {
      const hasActive = activeDownloads.some(d => 
        d.status === 'pending' || d.status === 'processing'
      );
      if (hasActive) {
        fetchActiveDownloads();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchActiveDownloads, activeDownloads.length]);

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('download-jobs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'download_jobs'
        },
        () => {
          fetchActiveDownloads();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchActiveDownloads]);

  return {
    activeDownloads,
    loading,
    startBackgroundDownload,
    downloadCompletedZip,
    deleteDownloadJob,
    getDownloadUrl,
    refreshDownloads: fetchActiveDownloads
  };
};
